import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import HeaderOne from './HeaderOne';
import FooterOne from './BottomFooter'; // Import your footer component

const ComparisonPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const initialMaterialId = searchParams.get('material_id');
    const productTypeId = searchParams.get('product_type_id');
    const customizationDataStr = searchParams.get('customizations');
    const initialWidth = searchParams.get('width') || 36;
    const initialHeight = searchParams.get('height') || 24;
    
    const [comparisonData, setComparisonData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const [leftMaterial, setLeftMaterial] = useState(null);
    const [rightMaterial, setRightMaterial] = useState(null);
    const [width, setWidth] = useState(initialWidth);
    const [height, setHeight] = useState(initialHeight);
    const [leftPrice, setLeftPrice] = useState(0);
    const [rightPrice, setRightPrice] = useState(0);
    
    const [customizations, setCustomizations] = useState({});

    useEffect(() => {
        const fetchComparison = async () => {
            if (!initialMaterialId || !productTypeId) {
                setError("Missing comparison parameters");
                setIsLoading(false);
                return;
            }

            try {
                const API_URL = `${process.env.REACT_APP_API_URL}`;
                const customizationData = customizationDataStr ? JSON.parse(decodeURIComponent(customizationDataStr)) : {};

                const response = await axios.post(
                    `${API_URL}/comparisons/compare_materials/`,
                    {
                        product_type_id: parseInt(productTypeId),
                        material_id: parseInt(initialMaterialId),
                        width_inches: parseFloat(initialWidth),
                        height_inches: parseFloat(initialHeight),
                        customization_data: customizationData
                    },
                    { headers: { 'Content-Type': 'application/json' } }
                );

                setComparisonData(response.data);
                setLeftMaterial(response.data.selected_material);
                
                const initialCustom = {};
                response.data.customizations.forEach((custom, idx) => {
                    initialCustom[idx] = {
                        value: custom.value,
                        price_modifier_flat: custom.price_modifier_flat,
                        price_modifier_percent: custom.price_modifier_percent
                    };
                });
                setCustomizations(initialCustom);
                
                const leftPriceVal = calculatePrice(response.data.selected_material, parseFloat(initialWidth), parseFloat(initialHeight), initialCustom);
                setLeftPrice(leftPriceVal);
                
                setError(null);
                setIsLoading(false);

            } catch (error) {
                console.error("Error fetching comparison:", error);
                setError(error.response?.data?.error || error.message || "Failed to load comparison");
                setIsLoading(false);
            }
        };

        fetchComparison();
    }, [initialMaterialId, productTypeId, customizationDataStr, initialWidth, initialHeight]);

    const calculatePrice = (material, w, h, customizationMap) => {
        if (!material) return 0;
        
        const area_sqft = (parseFloat(w) * parseFloat(h)) / 144.0;
        let price = parseFloat(material.cost_per_sqft) * area_sqft;

        Object.values(customizationMap).forEach(custom => {
            if (custom.price_modifier_flat) {
                price += parseFloat(custom.price_modifier_flat);
            }
            if (custom.price_modifier_percent) {
                price += (price * parseFloat(custom.price_modifier_percent) / 100);
            }
        });

        return Math.round(price * 100) / 100;
    };

    const handleCustomizationChange = (fieldIdx, newOption) => {
        const updated = {
            ...customizations,
            [fieldIdx]: newOption
        };
        setCustomizations(updated);
        
        if (leftMaterial) {
            const newLeftPrice = calculatePrice(leftMaterial, width, height, updated);
            setLeftPrice(newLeftPrice);
        }
        if (rightMaterial) {
            const newRightPrice = calculatePrice(rightMaterial, width, height, updated);
            setRightPrice(newRightPrice);
        }
    };

    const handleDimensionChange = (type, value) => {
        const newValue = parseFloat(value) || 0;
        if (newValue <= 0) return;
        
        if (type === 'width') setWidth(newValue);
        if (type === 'height') setHeight(newValue);
        
        const newW = type === 'width' ? newValue : width;
        const newH = type === 'height' ? newValue : height;
        
        if (leftMaterial) {
            const newLeftPrice = calculatePrice(leftMaterial, newW, newH, customizations);
            setLeftPrice(newLeftPrice);
        }
        if (rightMaterial) {
            const newRightPrice = calculatePrice(rightMaterial, newW, newH, customizations);
            setRightPrice(newRightPrice);
        }
    };

    const handleSelectMaterial = (material) => {
        if (rightMaterial?.id === material.id) {
            setRightMaterial(null);
        } else {
            setRightMaterial(material);
            const newPrice = calculatePrice(material, width, height, customizations);
            setRightPrice(newPrice);
        }
    };

    const handleDropdownSelect = (materialId) => {
        const material = comparisonData.similar_materials.find(m => m.id === parseInt(materialId));
        if (material) {
            handleSelectMaterial(material);
        }
    };
        const areaSize = comparisonData ? Math.round((width * height / 144) * 100) / 100 : 0;

    return(
        <>
            <HeaderOne />
            
            <div className="comparison-page">
                {isLoading && (
                    <div className="container">
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading comparison...</p>
                        </div>
                    </div>
                )}

                {error && !isLoading && (
                    <div className="container py-80">
                        <div className="error-state">
                            <i className="ph ph-warning"></i>
                            <h3>Error</h3>
                            <p>{error}</p>
                            <Link to="/" className="btn btn-primary">Go Back</Link>
                        </div>
                    </div>
                )}

                {!isLoading && !error && comparisonData && (
                    <section className="py-80">
                        <div className="container">
                            <div className="comparison-header">
                                <h2><i className="ph ph-shuffle"></i> Compare Materials</h2>
                                <Link to="/" className="btn btn-primary">
                                    <i className="ph ph-arrow-left"></i> Back
                                </Link>
                            </div>

                            {/* MAIN WRAPPER */}
                            <div className="main-wrapper">
                                {/* LEFT SECTION */}
                                <div className="left-section">
                                    {/* SELECTED MATERIALS */}
                                    <div className="materials-compact-section">
                                        <div className="compact-materials-display">
                                            {/* LEFT MATERIAL */}
                                            <div className="material-compact-card active">
                                                {leftMaterial && (
                                                    <>
                                                        {leftMaterial.image_swatch && (
                                                            <img src={leftMaterial.image_swatch} alt={leftMaterial.name} />
                                                        )}
                                                        <h5>{leftMaterial.name}</h5>
                                                        <p className="compact-cost">${leftMaterial.cost_per_sqft}/sq ft</p>
                                                        <div className="compact-price">${leftPrice}</div>
                                                    </>
                                                )}
                                            </div>

                                            {/* VS */}
                                            <div className="compact-vs">VS</div>

                                            {/* RIGHT MATERIAL */}
                                            <div className="material-compact-card">
                                                {rightMaterial ? (
                                                    <>
                                                        {rightMaterial.image_swatch && (
                                                            <img src={rightMaterial.image_swatch} alt={rightMaterial.name} />
                                                        )}
                                                        <h5>{rightMaterial.name}</h5>
                                                        <p className="compact-cost">${rightMaterial.cost_per_sqft}/sq ft</p>
                                                        <div className="compact-price">${rightPrice}</div>
                                                        {leftPrice > 0 && (
                                                            <div className={`compact-diff ${rightPrice < leftPrice ? 'save' : 'extra'}`}>
                                                                {rightPrice < leftPrice ? '✓' : '+'} ${Math.abs(rightPrice - leftPrice).toFixed(2)}
                                                            </div>
                                                        )}
                                                        <button 
                                                            className="btn-change-compact"
                                                            onClick={() => setRightMaterial(null)}
                                                        >
                                                            Change
                                                        </button>
                                                    </>
                                                ) : (
                                                    <div className="material-select-dropdown">
                                                        <label>Select Material</label>
                                                        <select 
                                                            className="material-dropdown-inline"
                                                            onChange={(e) => handleDropdownSelect(e.target.value)}
                                                            defaultValue=""
                                                        >
                                                            <option value="" disabled>Choose Material</option>
                                                            {comparisonData.similar_materials && comparisonData.similar_materials.map((material) => (
                                                                <option key={material.id} value={material.id}>
                                                                    {material.name} - ${material.cost_per_sqft}/sq ft
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* SPECIFICATIONS */}
                                    <div className="specifications-section">
                                        <div className="spec-title">Specifications</div>
                                        <div className="spec-controls">
                                            <div className="spec-input">
                                                <label>Width</label>
                                                <input 
                                                    type="number" 
                                                    value={width}
                                                    onChange={(e) => handleDimensionChange('width', e.target.value)}
                                                    min="1"
                                                />
                                            </div>
                                            <div className="spec-input">
                                                <label>Height</label>
                                                <input 
                                                    type="number" 
                                                    value={height}
                                                    onChange={(e) => handleDimensionChange('height', e.target.value)}
                                                    min="1"
                                                />
                                            </div>
                                            <div className="spec-area">
                                                <label>Area</label>
                                                <div className="spec-area-value">{areaSize} sq ft</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* RIGHT SECTION: CUSTOMIZATIONS */}
                                {comparisonData.customizations && comparisonData.customizations.length > 0 && (
                                    <div className="customizations-section">
                                        <div className="custom-title">Customizations</div>
                                        
                                        <div className="custom-fields">
                                            {comparisonData.customizations.map((custom, idx) => (
                                                <div key={idx} className="custom-field-item">
                                                    <label>{custom.field_name}</label>
                                                    <select 
                                                        value={customizations[idx]?.value || ''}
                                                        onChange={(e) => {
                                                            handleCustomizationChange(idx, {
                                                                value: e.target.value,
                                                                price_modifier_flat: custom.price_modifier_flat,
                                                                price_modifier_percent: custom.price_modifier_percent
                                                            });
                                                        }}
                                                        className="custom-select"
                                                    >
                                                        <option value="">{custom.field_name}</option>
                                                        <option value={custom.value}>{custom.value}</option>
                                                    </select>
                                                    {custom.price_modifier_flat > 0 && (
                                                        <span className="mod-price">+${custom.price_modifier_flat}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* SIMILAR MATERIALS */}
                            {comparisonData.similar_materials && comparisonData.similar_materials.length > 0 && (
                                <>
                                    <div className="section-title">
                                        <h3>Available Materials to Compare</h3>
                                    </div>
                                    
                                    <div className="similar-materials-section">
                                        <div className="materials-grid">
                                            {comparisonData.similar_materials.map((material) => (
                                                <div 
                                                    key={material.id} 
                                                    className={`material-grid-card ${rightMaterial?.id === material.id ? 'selected' : ''}`}
                                                    onClick={() => handleSelectMaterial(material)}
                                                >
                                                    {material.image_swatch && (
                                                        <img src={material.image_swatch} alt={material.name} />
                                                    )}
                                                    <h5>{material.name}</h5>
                                                    <p className="grid-cost">${material.cost_per_sqft}/sq ft</p>
                                                    <div className="grid-select-btn">
                                                        {rightMaterial?.id === material.id ? '✓ Selected' : 'Select'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                )}
            </div>
                        <style jsx>{`
                .comparison-page {
                    background: #f8f9fa;
                    min-height: 100vh;
                }

                .comparison-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 40px;
                }

                .comparison-header h2 {
                    margin: 0;
                    font-size: 28px;
                    color: #333;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .btn {
                    padding: 10px 20px;
                    border: none;
                    border-radius: 5px;
                    cursor: pointer;
                    background: #d97706;
                    color: white;
                    text-decoration: none;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .btn:hover {
                    background: #b45309;
                }

                /* MAIN WRAPPER - LEFT AND RIGHT */
                .main-wrapper {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 30px;
                    margin-bottom: 50px;
                }

                .left-section {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                /* MATERIALS SECTION */
                .materials-compact-section {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }

                .compact-materials-display {
                    display: grid;
                    grid-template-columns: 1fr auto 1fr;
                    gap: 12px;
                    align-items: center;
                }

                .material-compact-card {
                    background: #f8f9fa;
                    border: 2px solid #e5e7eb;
                    border-radius: 10px;
                    padding: 12px;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                }

                .material-compact-card.active {
                    border-color: #d97706;
                    background: #fffbf0;
                }

                .material-compact-card img {
                    width: 100%;
                    height: 120px;
                    object-fit: cover;
                    border-radius: 6px;
                }

                .material-compact-card h5 {
                    margin: 0;
                    font-size: 14px;
                    color: #333;
                    font-weight: 600;
                }

                .compact-cost {
                    font-size: 12px;
                    color: #666;
                    margin: 0;
                }

                .compact-price {
                    font-size: 16px;
                    font-weight: 700;
                    color: #d97706;
                }

                .compact-diff {
                    font-size: 11px;
                    font-weight: 600;
                    padding: 4px 8px;
                    border-radius: 4px;
                }

                .compact-diff.save {
                    background: #d4edda;
                    color: #155724;
                }

                .compact-diff.extra {
                    background: #f8d7da;
                    color: #721c24;
                }

                .compact-vs {
                    font-weight: 700;
                    font-size: 14px;
                    color: #d97706;
                    text-align: center;
                }

                .material-select-dropdown {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                    padding: 20px 10px;
                    min-height: 200px;
                    justify-content: center;
                }

                .material-select-dropdown label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #333;
                }

                .material-dropdown-inline {
                    width: 100%;
                    padding: 10px 8px;
                    border: 2px solid #d97706;
                    border-radius: 6px;
                    font-size: 13px;
                    background: white;
                    cursor: pointer;
                    color: #333;
                }

                .material-dropdown-inline:focus {
                    outline: none;
                    border-color: #b45309;
                    background: #fffbf0;
                }

                .btn-change-compact {
                    padding: 6px 12px;
                    background: #e5e7eb;
                    color: #333;
                    border: none;
                    border-radius: 5px;
                    cursor: pointer;
                    font-size: 11px;
                    font-weight: 600;
                    transition: all 0.3s;
                }

                .btn-change-compact:hover {
                    background: #d1d5db;
                }

                /* SPECIFICATIONS SECTION */
                .specifications-section {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }

                .spec-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #333;
                    margin-bottom: 15px;
                    border-bottom: 2px solid #d97706;
                    padding-bottom: 10px;
                }

                .spec-controls {
                    display: grid;
                    grid-template-columns: 1fr 1fr auto;
                    gap: 15px;
                }

                .spec-input {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .spec-input label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #333;
                }

                .spec-input input {
                    padding: 10px;
                    border: 2px solid #ddd;
                    border-radius: 6px;
                    font-size: 14px;
                }

                .spec-input input:focus {
                    outline: none;
                    border-color: #d97706;
                }

                .spec-area {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .spec-area label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #333;
                }

                .spec-area-value {
                    padding: 10px;
                    background: #f0f9ff;
                    border-radius: 6px;
                    color: #d97706;
                    font-weight: 600;
                    font-size: 14px;
                    text-align: center;
                }

                /* CUSTOMIZATIONS SECTION */
                .customizations-section {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                    height: fit-content;
                    position: sticky;
                    top: 20px;
                }

                .custom-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #333;
                    border-bottom: 2px solid #d97706;
                    padding-bottom: 10px;
                }

                .custom-fields {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .custom-field-item {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .custom-field-item label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #333;
                }

                .custom-select {
                    padding: 10px;
                    border: 2px solid #ddd;
                    border-radius: 6px;
                    font-size: 13px;
                    background: white;
                    cursor: pointer;
                }

                .custom-select:focus {
                    outline: none;
                    border-color: #d97706;
                }

                .mod-price {
                    font-size: 11px;
                    color: #d97706;
                    font-weight: 600;
                }

                /* SIMILAR MATERIALS */
                .section-title {
                    margin-top: 40px;
                    margin-bottom: 20px;
                    border-bottom: 3px solid #d97706;
                    padding-bottom: 12px;
                }

                .section-title h3 {
                    margin: 0;
                    font-size: 20px;
                    color: #333;
                    font-weight: 700;
                }

                .similar-materials-section {
                    background: white;
                    border-radius: 12px;
                    padding: 25px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }

                .materials-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
                    gap: 20px;
                }

                .material-grid-card {
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 10px;
                    padding: 15px;
                    cursor: pointer;
                    transition: all 0.3s;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                }

                .material-grid-card:hover {
                    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
                    transform: translateY(-3px);
                }

                .material-grid-card.selected {
                    border-color: #d97706;
                    background: #fffbf0;
                }

                .material-grid-card img {
                    width: 100%;
                    height: 140px;
                    object-fit: cover;
                    border-radius: 8px;
                    margin-bottom: 10px;
                }

                .material-grid-card h5 {
                    margin: 8px 0 4px 0;
                    font-size: 14px;
                    color: #333;
                    font-weight: 600;
                }

                .grid-cost {
                    color: #666;
                    font-size: 12px;
                    margin: 0 0 10px 0;
                }

                .grid-select-btn {
                    padding: 8px 12px;
                    background: #d97706;
                    color: white;
                    border-radius: 6px;
                    font-weight: 600;
                    font-size: 12px;
                    transition: all 0.3s;
                    width: 100%;
                }

                .material-grid-card.selected .grid-select-btn {
                    background: #28a745;
                }

                @media (max-width: 1024px) {
                    .main-wrapper {
                        grid-template-columns: 1fr;
                    }

                    .customizations-section {
                        position: static;
                    }

                    .materials-grid {
                        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
                    }
                }

                .loading-state, .error-state {
                    text-align: center;
                    padding: 60px 20px;
                }

                .spinner {
                    display: inline-block;
                    width: 40px;
                    height: 40px;
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #d97706;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        
            <FooterOne />
        </>
    );
};



export default ComparisonPage;